/**
 * <NavAnchor> — a client-side link to a route path that is a plain `string`.
 *
 * WHY NOT `<Link to=…>`. TanStack's `<Link>` requires `to` and types it as the
 * union of routes present in `routeTree.gen.ts`. The screens this shell links
 * to (`/sheets`, `/workers`, `/rates`, `/payroll`, …) are built in parallel by
 * other agents, so that union is incomplete until the last one lands and a
 * typed `to` would fail the build in the meantime. `<Link href=…>` does not
 * help: `href` is additive, `to` stays required.
 *
 * So the shell renders a real `<a href>` — correct middle-click, cmd-click,
 * "copy link address" and screen-reader semantics — and intercepts the plain
 * left-click to push through `router.history`, which is typed `(path: string)`
 * and therefore route-table independent. Navigation is still fully SPA.
 *
 * SCREEN AUTHORS: inside a single screen, use `<Link to=…>`. It is typed
 * because of the `Register` augmentation in main.tsx, and typed is better.
 * This component exists only for the cross-screen shell.
 */
import { useRouter } from '@tanstack/react-router';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';

export interface NavAnchorProps {
  to: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  title?: string;
  'aria-current'?: 'page' | undefined;
}

export function NavAnchor({ to, children, className, style, title, ...rest }: NavAnchorProps) {
  const router = useRouter();

  function onClick(e: MouseEvent<HTMLAnchorElement>): void {
    /* Let the browser handle anything that is not a plain left-click. */
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    router.history.push(to);
  }

  return (
    <a
      href={to}
      onClick={onClick}
      className={className}
      style={style}
      title={title}
      aria-current={rest['aria-current']}
    >
      {children}
    </a>
  );
}
